import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import WorkflowProgress from './WorkflowProgress';
import { TicketData } from '../TicketDetail';
import AttachmentTab from '../../../components/AttachmentTab';
import TabLoadingSpinner from '../../../components/TabLoadingSpinner';
import RichTextEditor from '../../../components/RichTextEditor';
import RichTextDisplay from '../../../components/RichTextDisplay';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { stripHtml } from '../../../utils/sanitize';
import {
  CalendarIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { SreInvestigateActions } from '../../../features/sre-investigation';
import type { TicketEntityType } from '../../../features/sre-investigation';
import { resolveTicketAlertSource } from '../../../features/sre-investigation/utils/ticketIdentity';

function mapTicketTypeToEntityType(ticketType?: string | null): TicketEntityType {
  switch ((ticketType || '').toUpperCase()) {
    case 'INCIDENT':
      return 'incident';
    case 'ALERT':
      return 'alert';
    case 'CASE':
    case 'SERVICE_REQUEST':
    default:
      return 'case';
  }
}

interface ChangeUnifiedTemplateProps {
  ticket: TicketData;
  isReadOnly: boolean;
  activeTab: 'details' | 'approvals' | 'notes' | 'activity' | 'assessment' | 'resolution' | 'attachments';
  setActiveTab: (tab: 'details' | 'approvals' | 'notes' | 'activity' | 'assessment' | 'resolution' | 'attachments') => void;
  onRefresh: () => void;
}

type ChangeTabType = 'details' | 'planning' | 'schedule' | 'approvals' | 'notes' | 'activity' | 'closure' | 'assessment' | 'resolution' | 'attachments';

const priorityColors: Record<string, string> = {
  'critical_1': 'bg-red-100 text-red-800 border-red-200',
  'high_2': 'bg-orange-100 text-orange-800 border-orange-200',
  'moderate_3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'low_4': 'bg-blue-100 text-blue-800 border-blue-200',
  'planning_5': 'bg-gray-100 text-gray-800 border-gray-200',
  CRITICAL: 'bg-red-100 text-red-800 border-red-200',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  LOW: 'bg-blue-100 text-blue-800 border-blue-200'
};

const priorityLabels: Record<string, string> = {
  'critical_1': 'Critical',
  'high_2': 'High',
  'moderate_3': 'Moderate',
  'low_4': 'Low',
  'planning_5': 'Planning',
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low'
};

const ChangeUnifiedTemplate: React.FC<ChangeUnifiedTemplateProps> = ({
  ticket,
  isReadOnly,
  activeTab,
  setActiveTab,
  onRefresh
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState<ChangeTabType>('details');
  const { user, isContact, loading } = useAuth();
  const { formatDate, formatDateTime } = useDateFormatter();

  // Sync with parent's activeTab when it changes
  React.useEffect(() => {
    // Only sync if parent activeTab is one of the standard tabs that also exist in Change template
    if (activeTab === 'details' || activeTab === 'notes' || activeTab === 'activity') {
      // Don't override Change-specific tabs (planning, schedule, approvals)
      if (internalActiveTab !== 'planning' && internalActiveTab !== 'schedule' && internalActiveTab !== 'approvals') {
        setInternalActiveTab(activeTab as ChangeTabType);
      }
    }
  }, [activeTab, internalActiveTab]);
  const queryClient = useQueryClient();
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalModalData, setApprovalModalData] = useState<{decision: 'approved' | 'rejected', comments: string}>({decision: 'approved', comments: ''});

  // Customer approval states
  const [showCustomerApprovalModal, setShowCustomerApprovalModal] = useState(false);
  const [customerApprovalDecision, setCustomerApprovalDecision] = useState<'approved' | 'rejected'>('approved');
  const [customerApprovalComments, setCustomerApprovalComments] = useState('');

  // Helper function to check if change state is assess or higher (for showing Approval tab)
  const isChangeStateAssessOrHigher = (changeState?: string) => {
    const assessOrHigherStates = ['assess', 'authorize', 'scheduled', 'implement', 'review', 'closed'];
    return changeState ? assessOrHigherStates.includes(changeState.toLowerCase()) : false;
  };

  // Fetch approval groups only when the Approvals tab is open
  useQuery({
    queryKey: ['groups', 'approval'],
    queryFn: async () => {
      const response = await api.get('/groups?type=approval');
      return response.data;
    },
    enabled: internalActiveTab === 'approvals' && ticket.ticketType === 'CHANGE' && !loading && !isContact()
  });

  // Fetch members of the approval group
  const { data: approvalGroupMembersData } = useQuery({
    queryKey: ['group-members', ticket.approvalGroup?.id],
    queryFn: async () => {
      if (!ticket.approvalGroup?.id) return [];
      const response = await api.get(`/groups/${ticket.approvalGroup.id}/members`);
      return response.data;
    },
    enabled: internalActiveTab === 'approvals' && ticket.ticketType === 'CHANGE' && !!ticket.approvalGroup?.id && !loading && !isContact()
  });

  // Fetch change details with approvals
  const { data: changeDetails } = useQuery({
    queryKey: ['change-approvals', ticket.id],
    queryFn: async () => {
      const response = await api.get(`/changes/${ticket.id}`);
      return response.data;
    },
    enabled: internalActiveTab === 'approvals' && ticket.ticketType === 'CHANGE',
    refetchInterval: internalActiveTab === 'approvals' ? 15000 : false,
  });

  const { data: workNotes = [], isLoading: isLoadingNotes } = useQuery({
    queryKey: ['case', ticket.id, 'notes'],
    queryFn: async () => {
      const response = await api.get(`/cases/${ticket.id}/work-notes`);
      return response.data;
    },
    enabled: internalActiveTab === 'notes',
  });

  const { data: caseActivities = [], isLoading: isLoadingActivities } = useQuery({
    queryKey: ['case-activities', ticket.id],
    queryFn: async () => {
      const response = await api.get(`/cases/${ticket.id}/activities`);
      return response.data;
    },
    enabled: internalActiveTab === 'activity' && !isContact(),
  });

  // Approval mutation
  const approvalMutation = useMutation({
    mutationFn: async (data: { decision: 'approved' | 'rejected', comments: string }) => {
      const response = await api.post(`/changes/${ticket.id}/approval`, {
        decision: data.decision,
        comments: data.comments
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticket.id] });
      queryClient.invalidateQueries({ queryKey: ['change-approvals', ticket.id] });
      setShowApprovalModal(false);
      onRefresh();
    },
    onError: (error: any) => {
      console.error('Approval error:', error);
      alert(error.response?.data?.message || 'Failed to process approval');
    }
  });

  // Customer approval mutation
  const customerApprovalMutation = useMutation({
    mutationFn: async (data: { decision: 'approved' | 'rejected', comments: string }) => {
      const response = await api.post(`/changes/${ticket.id}/customer-approval/respond`, {
        decision: data.decision,
        comments: data.comments
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticket.id] });
      setShowCustomerApprovalModal(false);
      setCustomerApprovalComments('');
      onRefresh();
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Failed to process customer approval');
    }
  });


  // Check approval status
  const getApprovalStatus = () => {
    if (!changeDetails?.approvals) return { approved: 0, rejected: 0, pending: 0 };

    const approvals = changeDetails.approvals;
    const approved = approvals.filter((a: any) => a.state === 'approved').length;
    const rejected = approvals.filter((a: any) => a.state === 'rejected').length;
    const pending = approvals.filter((a: any) => a.state === 'pending').length;

    return { approved, rejected, pending };
  };

  const approvalStatus = getApprovalStatus();

  // Format change type
  const formatChangeType = (type: string | undefined): string => {
    if (!type) return 'Not specified';
    const changeTypeMap: Record<string, string> = {
      'standard': 'Standard',
      'normal': 'Normal',
      'emergency': 'Emergency'
    };
    return changeTypeMap[type.toLowerCase()] || type;
  };

  // Format risk
  const formatRisk = (risk: string | undefined): string => {
    if (!risk) return 'Not specified';
    const riskMap: Record<string, string> = {
      'high': 'High',
      'medium': 'Medium',
      'moderate': 'Medium',
      'low': 'Low'
    };
    return riskMap[risk.toLowerCase()] || risk;
  };

  // Format impact
  const formatImpact = (impact: string | undefined): string => {
    if (!impact) return 'Not specified';
    // Handle format like "low_3" or just "low"
    if (impact.includes('_')) {
      const [level] = impact.split('_');
      return level.charAt(0).toUpperCase() + level.slice(1);
    }
    return impact.charAt(0).toUpperCase() + impact.slice(1);
  };

  // Get type-specific fields based on ticket type
  const renderTypeSpecificFields = () => {
    switch (ticket.ticketType) {
      case 'INCIDENT':
        return (
          <>
            {ticket.resolution && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Resolution</label>
                <div className="mt-1 text-sm text-gray-900">
                  <RichTextDisplay content={ticket.resolution} />
                </div>
              </div>
            )}
            {ticket.resolutionCode && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Resolution Code</label>
                <p className="mt-1 text-sm text-gray-900">{ticket.resolutionCode}</p>
              </div>
            )}
          </>
        );
      case 'PROBLEM':
        return (
          <>
            {ticket.problemStatement && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Problem Statement</label>
                <div className="mt-1 text-sm text-gray-900">
                  <RichTextDisplay content={ticket.problemStatement} />
                </div>
              </div>
            )}
            {ticket.rootCause && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Root Cause</label>
                <div className="mt-1 text-sm text-gray-900">
                  <RichTextDisplay content={ticket.rootCause} />
                </div>
              </div>
            )}
            {ticket.workaround && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Workaround</label>
                <div className="mt-1 text-sm text-gray-900">
                  <RichTextDisplay content={ticket.workaround} />
                </div>
              </div>
            )}
          </>
        );
      case 'CHANGE':
        return (
          <>
            {ticket.changeType && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Change Type</label>
                <p className="mt-1 text-sm text-gray-900">{formatChangeType(ticket.changeType)}</p>
              </div>
            )}
            {ticket.changeRisk && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Risk Level</label>
                <p className="mt-1 text-sm text-gray-900">{formatRisk(ticket.changeRisk)}</p>
              </div>
            )}
            {ticket.plannedStartDate && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Planned Start</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(ticket.plannedStartDate)}</p>
              </div>
            )}
            {ticket.plannedEndDate && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Planned End</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(ticket.plannedEndDate)}</p>
              </div>
            )}
            {ticket.implementationPlan && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Implementation Plan</label>
                <div className="mt-1 text-sm text-gray-900">
                  <RichTextDisplay content={ticket.implementationPlan} />
                </div>
              </div>
            )}
          </>
        );
      case 'CASE':
        return (
          <>
            {ticket.channel && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Channel</label>
                <p className="mt-1 text-sm text-gray-900">{ticket.channel}</p>
              </div>
            )}
            {ticket.contactType && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Type</label>
                <p className="mt-1 text-sm text-gray-900">{ticket.contactType}</p>
              </div>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* 1. Workflow Progress at the very top */}
      <WorkflowProgress
        ticketType={ticket.ticketType}
        currentState={ticket.state}
        problemState={ticket.problemState}
        changeState={ticket.changeState}
      />

      {/* Title Section - matches unified ticket layout */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-sm font-semibold text-gray-600 whitespace-nowrap shrink-0">Title:</span>
          <p className="text-sm text-gray-900 leading-relaxed break-words min-w-0 flex-[1_1_12rem]">
            {stripHtml(ticket.shortDescription) || '-'}
          </p>
          <div className="shrink-0 flex flex-wrap items-center gap-2">
            <SreInvestigateActions
              ticketNumber={ticket.number}
              entityType={mapTicketTypeToEntityType(ticket.ticketType)}
              entityId={ticket.id}
              engagementId={ticket.Engagement?.id}
              title={stripHtml(ticket.shortDescription) || undefined}
              description={typeof ticket.description === 'string' ? stripHtml(ticket.description) : undefined}
              node={ticket.node || ticket.Resource?.name || undefined}
              computer={ticket.node || ticket.Resource?.name || undefined}
              resourceId={
                ticket.Resource?.resourceId ||
                ticket.resourceIdValue ||
                undefined
              }
              resourceGroup={ticket.Resource?.resourceGroupName || undefined}
              vmName={ticket.Resource?.name || undefined}
              ticketState={ticket.state}
              sreInvestigationStatus={ticket.sreInvestigationStatus}
              sreDecisionReason={ticket.sreDecisionReason}
              sreEvaluatedAt={ticket.sreEvaluatedAt}
              sreRunId={ticket.sreRunId}
              customerName={ticket.Customer?.accountName}
              engagementName={ticket.Engagement?.name}
              source={resolveTicketAlertSource(ticket)}
              onPosted={onRefresh}
              forceShow
              autoStart={false}
            />
          </div>
        </div>
      </div>

      {/* Customer Approval Banner - Pending */}
      {ticket.customerApprovalRequested &&
       ticket.customerApprovalStatus === 'pending' &&
       isContact() &&
       (user?.id === ticket.contactId || (!ticket.contactId && user?.engagementId === ticket.Engagement?.id)) && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">Customer Approval Required</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>This change request requires your approval. Please review the details and provide your decision.</p>
              </div>
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setCustomerApprovalDecision('approved');
                    setShowCustomerApprovalModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCustomerApprovalDecision('rejected');
                    setShowCustomerApprovalModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Approval Banner - Approved */}
      {ticket.customerApprovalRequested &&
       ticket.customerApprovalStatus === 'approved' &&
       isContact() &&
       (user?.id === ticket.contactId || (!ticket.contactId && user?.engagementId === ticket.Engagement?.id)) && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-green-800">Customer Approval Granted</h3>
              <div className="mt-2 text-sm text-green-700">
                <p>This change request has been approved by the customer.</p>
                {ticket.User_Case_customerApprovedByIdToUser && (
                  <div className="mt-2">
                    <p className="font-medium">Approved by:</p>
                    <p className="mt-1">
                      {ticket.User_Case_customerApprovedByIdToUser.firstName} {ticket.User_Case_customerApprovedByIdToUser.lastName}
                      {' '}({ticket.User_Case_customerApprovedByIdToUser.email})
                    </p>
                  </div>
                )}
                {ticket.customerApprovalComments && (
                  <div className="mt-2">
                    <p className="font-medium">Comments:</p>
                    <div className="mt-1 italic">
                      <RichTextDisplay content={ticket.customerApprovalComments} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Approval Banner - Rejected */}
      {ticket.customerApprovalRequested &&
       ticket.customerApprovalStatus === 'rejected' &&
       isContact() &&
       (user?.id === ticket.contactId || (!ticket.contactId && user?.engagementId === ticket.Engagement?.id)) && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">Customer Approval Rejected</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>This change request has been rejected by the customer.</p>
                {ticket.User_Case_customerApprovedByIdToUser && (
                  <div className="mt-2">
                    <p className="font-medium">Rejected by:</p>
                    <p className="mt-1">
                      {ticket.User_Case_customerApprovedByIdToUser.firstName} {ticket.User_Case_customerApprovedByIdToUser.lastName}
                      {' '}({ticket.User_Case_customerApprovedByIdToUser.email})
                    </p>
                  </div>
                )}
                {ticket.customerApprovalComments && (
                  <div className="mt-2">
                    <p className="font-medium">Rejection Reason:</p>
                    <div className="mt-1 italic">
                      <RichTextDisplay content={ticket.customerApprovalComments} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. Main content area with tabs and sidebar */}
      <div className={`grid grid-cols-1 ${ticket.ticketType === 'CHANGE' ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6`}>
        {/* Left side: Tabs section */}
        <div className={ticket.ticketType === 'CHANGE' ? "lg:col-span-3" : "lg:col-span-2"}>
          <div className="bg-white shadow rounded-lg">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex justify-start px-4 overflow-x-auto">
                <button
                  onClick={() => {
                    setInternalActiveTab('details');
                    setActiveTab('details');
                  }}
                  className={`py-2 px-3 border-b-2 font-medium text-sm mr-2 whitespace-nowrap ${
                    internalActiveTab === 'details'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Details
                </button>
                {ticket.ticketType === 'CHANGE' && isChangeStateAssessOrHigher(ticket.changeState) && (
                  <button
                    onClick={() => {
                      setInternalActiveTab('approvals');
                      setActiveTab('approvals');
                    }}
                    className={`py-2 px-3 border-b-2 font-medium text-sm mr-2 whitespace-nowrap ${
                      internalActiveTab === 'approvals'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Approvals
                  </button>
                )}
                {ticket.ticketType === 'CHANGE' && (
                  <button
                    onClick={() => setInternalActiveTab('planning')}
                    className={`py-2 px-3 border-b-2 font-medium text-sm mr-2 whitespace-nowrap ${
                      internalActiveTab === 'planning'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Planning
                  </button>
                )}
                {ticket.ticketType === 'CHANGE' && (
                  <button
                    onClick={() => setInternalActiveTab('schedule')}
                    className={`py-2 px-3 border-b-2 font-medium text-sm mr-2 whitespace-nowrap ${
                      internalActiveTab === 'schedule'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Schedule
                  </button>
                )}
                <button
                  onClick={() => {
                    setInternalActiveTab('notes');
                    setActiveTab('notes');
                  }}
                  className={`py-2 px-3 border-b-2 font-medium text-sm mr-2 whitespace-nowrap ${
                    internalActiveTab === 'notes'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Work Notes
                </button>
                <button
                  onClick={() => {
                    setInternalActiveTab('activity');
                    setActiveTab('activity');
                  }}
                  className={`py-2 px-3 border-b-2 font-medium text-sm mr-2 whitespace-nowrap ${
                    internalActiveTab === 'activity'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Activity
                </button>
                {ticket.ticketType === 'CHANGE' && (
                  <button
                    onClick={() => setInternalActiveTab('closure')}
                    className={`py-2 px-3 border-b-2 font-medium text-sm mr-2 whitespace-nowrap ${
                      internalActiveTab === 'closure'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Closure
                  </button>
                )}
                <button
                  onClick={() => {
                    setInternalActiveTab('attachments');
                    setActiveTab('attachments');
                  }}
                  className={`py-2 px-3 border-b-2 font-medium text-sm mr-2 whitespace-nowrap ${
                    internalActiveTab === 'attachments'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Attachments
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {internalActiveTab === 'details' && (
                <div className="space-y-6">
                  {/* General Information */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">General Information</h3>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Number</label>
                        <p className="mt-1 text-sm text-gray-900">{ticket.number}</p>
                      </div>
                      {ticket.ticketType === 'CHANGE' ? (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Change Type</label>
                            <p className="mt-1 text-sm text-gray-900">{formatChangeType(ticket.changeType)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Risk Assessment</label>
                            <p className="mt-1 text-sm text-gray-900">{formatRisk(ticket.changeRisk)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Impact</label>
                            <p className="mt-1 text-sm text-gray-900">{formatImpact(ticket.impact)}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Priority</label>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[ticket.priority] || 'bg-gray-100 text-gray-800'}`}>
                              {priorityLabels[ticket.priority] || ticket.priority}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Type</label>
                            <p className="mt-1 text-sm text-gray-900">{ticket.ticketType.replace('_', ' ')}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Priority</label>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[ticket.priority] || 'bg-gray-100 text-gray-800'}`}>
                              {priorityLabels[ticket.priority] || ticket.priority}
                            </span>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Category</label>
                            <p className="mt-1 text-sm text-gray-900">{ticket.category || 'Not specified'}</p>
                          </div>
                          <div className="sm:col-span-2 lg:col-span-3">
                            <label className="block text-sm font-medium text-gray-700">Short Description</label>
                            <p className="mt-1 text-sm text-gray-900">{stripHtml(ticket.shortDescription)}</p>
                          </div>
                          <div className="sm:col-span-2 lg:col-span-3">
                            <label className="block text-sm font-medium text-gray-700">Description</label>
                            <div className="mt-1 text-sm text-gray-900">
                              <RichTextDisplay content={ticket.description || 'No description provided'} />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Type-specific fields - not shown for CHANGE type as they're in General Information */}
                  {ticket.ticketType !== 'CHANGE' && renderTypeSpecificFields() && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {renderTypeSpecificFields()}
                      </div>
                    </div>
                  )}

                  {/* Assignment Information */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Assignment</h3>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {ticket.ticketType === 'CHANGE' ? (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Assignment Group</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.AssignmentGroup?.name || 'No group assigned'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.assignedTo
                                ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}`
                                : 'Unassigned'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Approval Group</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.approvalGroup?.name || 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">CAB Required</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.cabRequired ? 'Yes' : 'No'}
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.assignedTo
                                ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}`
                                : 'Unassigned'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Assignment Group</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.AssignmentGroup?.name || 'No group assigned'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Caller</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.caller
                                ? `${ticket.caller.firstName} ${ticket.caller.lastName}`
                                : 'No caller'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Customer</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {ticket.Customer?.accountName || 'No customer'}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Approvals Tab - Only for CHANGE tickets in assess or higher states */}
              {internalActiveTab === 'approvals' && ticket.ticketType === 'CHANGE' && isChangeStateAssessOrHigher(ticket.changeState) && (
                <div className="space-y-6">
                  {/* Approval Group Information */}
                  {ticket.approvalGroup?.id && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">Approval Group</h4>
                      <p className="text-sm text-blue-800">
                        {ticket.approvalGroup?.name || 'No approval group assigned'}
                      </p>
                    </div>
                  )}

                  {/* Approval Group Members Table */}
                  {ticket.approvalGroup?.id && approvalGroupMembersData && approvalGroupMembersData.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-base font-medium text-gray-900">Approval Group Members</h4>
                      <div className="overflow-x-auto overflow-y-auto max-h-64 shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                User Name
                              </th>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email
                              </th>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {approvalGroupMembersData.map((member: any) => {
                              const memberApproval = changeDetails?.approvals?.find((a: any) => a.approverId === member.id);
                              const hasAnyRejection = changeDetails?.approvals?.some((a: any) => a.state === 'rejected');
                              // Super admin approval is when someone NOT in the approval group approves
                              const groupMemberIds = approvalGroupMembersData.map((m: any) => m.id);
                              const hasSuperAdminApproval = changeDetails?.approvals?.some((a: any) =>
                                a.state === 'approved' && !groupMemberIds.includes(a.approverId)
                              );

                              return (
                                <tr key={member.id}>
                                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                    {member.firstName} {member.lastName}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                                    {member.email}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    {memberApproval ? (
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                        memberApproval.state === 'approved'
                                          ? 'bg-green-100 text-green-800'
                                          : memberApproval.state === 'rejected'
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {memberApproval.state === 'approved' ? 'Approved' :
                                         memberApproval.state === 'rejected' ? 'Rejected' : 'Pending'}
                                      </span>
                                    ) : hasSuperAdminApproval ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        Approved by Super Admin
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                        Pending
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                                    {ticket.cabRequired && (!memberApproval || memberApproval.state === 'pending') &&
                                     !hasAnyRejection && !hasSuperAdminApproval &&
                                     ((user as any)?.role?.name === 'super_admin' || member.id === (user as any)?.id) ? (
                                      <div className="flex space-x-1">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setApprovalModalData({decision: 'approved', comments: ''});
                                            setShowApprovalModal(true);
                                          }}
                                          disabled={approvalMutation.isPending || isReadOnly}
                                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                        >
                                          {approvalMutation.isPending ? 'Processing...' : 'Approve'}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setApprovalModalData({decision: 'rejected', comments: ''});
                                            setShowApprovalModal(true);
                                          }}
                                          disabled={approvalMutation.isPending || isReadOnly}
                                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                        >
                                          {approvalMutation.isPending ? 'Processing...' : 'Reject'}
                                        </button>
                                      </div>
                                    ) : memberApproval ? (
                                      <div className="text-xs text-gray-500">
                                        {memberApproval.comments && (
                                          <div className="mb-1">
                                            <RichTextDisplay content={memberApproval.comments} />
                                          </div>
                                        )}
                                        {memberApproval.approvedAt && (
                                          <p>{formatDate(memberApproval.approvedAt)}</p>
                                        )}
                                      </div>
                                    ) : hasAnyRejection ? (
                                      <div className="text-xs text-red-600">
                                        Change rejected - No further approvals allowed
                                      </div>
                                    ) : hasSuperAdminApproval ? (
                                      <div className="text-xs text-green-600">
                                        Change approved by Super Admin
                                      </div>
                                    ) : (user as any)?.role?.name !== 'super_admin' && member.id !== (user as any)?.id ? (
                                      <div className="text-xs text-gray-500">
                                        Only the assigned user or super admin can approve
                                      </div>
                                    ) : null}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* No Members in Approval Group Message */}
                  {ticket.approvalGroup?.id && approvalGroupMembersData && approvalGroupMembersData.length === 0 && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.12M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.12M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 mb-1">No Members in Approval Group</h3>
                      <p className="text-sm text-gray-500">
                        The approval group has no members assigned.
                      </p>
                    </div>
                  )}

                  {/* No Approval Group Message */}
                  {!ticket.approvalGroup?.id && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 mb-1">No Approval Group Assigned</h3>
                      <p className="text-sm text-gray-500">
                        This change request does not have an approval group assigned.
                      </p>
                    </div>
                  )}

                  {/* Overall Approval Summary */}
                  {changeDetails?.approvals && changeDetails.approvals.length > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Approval Summary</h4>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-green-600">
                            {approvalStatus.approved}
                          </div>
                          <div className="text-sm text-gray-600">Approved</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-red-600">
                            {approvalStatus.rejected}
                          </div>
                          <div className="text-sm text-gray-600">Rejected</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-yellow-600">
                            {approvalStatus.pending}
                          </div>
                          <div className="text-sm text-gray-600">Pending</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {internalActiveTab === 'notes' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Work Notes</h3>
                  {isLoadingNotes ? (
                    <TabLoadingSpinner label="Loading work notes" />
                  ) : workNotes && workNotes.length > 0 ? (
                    <div className="space-y-3">
                      {workNotes
                        .filter((note: any) => isContact() ? note.isPublic : true)
                        .map((note: any, index: number) => (
                        <div key={note.id || index} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center">
                              <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900">
                                {note.User?.firstName} {note.User?.lastName}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {formatDate(note.createdAt || note.timestamp)}
                            </span>
                          </div>
                          <div className="mt-2 text-sm text-gray-700">
                            <RichTextDisplay content={note.note || note.message} />
                          </div>
                          {note.isPublic && (
                            <span className="mt-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Public
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No work notes available</p>
                  )}
                </div>
              )}

              {/* Planning Tab - Only for CHANGE tickets */}
              {internalActiveTab === 'planning' && ticket.ticketType === 'CHANGE' && (
                <div className="space-y-6">
                  {/* Justification */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Justification</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-700">
                        <RichTextDisplay content={ticket.justification || 'No justification provided'} />
                      </div>
                    </div>
                  </div>

                  {/* Risk & Impact Analysis */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Risk & Impact Analysis</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-700">
                        <RichTextDisplay content={ticket.riskImpactAnalysis || 'No risk/impact analysis provided'} />
                      </div>
                    </div>
                  </div>

                  {/* Implementation Plan */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Implementation Plan</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-700">
                        <RichTextDisplay content={ticket.implementationPlan || 'No implementation plan provided'} />
                      </div>
                    </div>
                  </div>

                  {/* Backout Plan */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Backout Plan</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-700">
                        <RichTextDisplay content={ticket.backoutPlan || ticket.rollbackPlan || 'No backout/rollback plan provided'} />
                      </div>
                    </div>
                  </div>

                  {/* Test Plan */}
                  {ticket.testPlan && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Test Plan</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-700">
                          <RichTextDisplay content={ticket.testPlan} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Schedule Tab - Only for CHANGE tickets */}
              {internalActiveTab === 'schedule' && ticket.ticketType === 'CHANGE' && (
                <div className="space-y-6">
                  {/* Planned Dates */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Planned Schedule</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Planned Start</label>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-900">
                            {ticket.plannedStart || ticket.plannedStartDate
                              ? formatDateTime(ticket.plannedStart || ticket.plannedStartDate || '')
                              : 'Not scheduled'}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Planned End</label>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-900">
                            {ticket.plannedEnd || ticket.plannedEndDate
                              ? formatDateTime(ticket.plannedEnd || ticket.plannedEndDate || '')
                              : 'Not scheduled'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actual Dates */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Actual Schedule</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Actual Start</label>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-900">
                            {ticket.actualStart
                              ? formatDateTime(ticket.actualStart)
                              : 'Not started'}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Actual End</label>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-900">
                            {ticket.actualEnd
                              ? formatDateTime(ticket.actualEnd)
                              : 'Not completed'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Duration Information */}
                  {(ticket.plannedStart || ticket.plannedStartDate) && (ticket.plannedEnd || ticket.plannedEndDate) && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Duration Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Duration</label>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-sm text-gray-900">
                              {(() => {
                                const start = new Date(ticket.plannedStart || ticket.plannedStartDate || '');
                                const end = new Date(ticket.plannedEnd || ticket.plannedEndDate || '');
                                const diffMs = end.getTime() - start.getTime();
                                const diffHours = Math.round(diffMs / (1000 * 60 * 60));
                                const diffDays = Math.floor(diffHours / 24);
                                const remainingHours = diffHours % 24;

                                if (diffDays > 0) {
                                  return remainingHours > 0 ? `${diffDays} days, ${remainingHours} hours` : `${diffDays} days`;
                                } else {
                                  return `${diffHours} hours`;
                                }
                              })()}
                            </p>
                          </div>
                        </div>
                        {ticket.actualStart && ticket.actualEnd && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Actual Duration</label>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-sm text-gray-900">
                                {(() => {
                                  const start = new Date(ticket.actualStart);
                                  const end = new Date(ticket.actualEnd);
                                  const diffMs = end.getTime() - start.getTime();
                                  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
                                  const diffDays = Math.floor(diffHours / 24);
                                  const remainingHours = diffHours % 24;

                                  if (diffDays > 0) {
                                    return remainingHours > 0 ? `${diffDays} days, ${remainingHours} hours` : `${diffDays} days`;
                                  } else {
                                    return `${diffHours} hours`;
                                  }
                                })()}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Schedule Status */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Schedule Status</h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-900">
                            {(() => {
                              if (!ticket.actualStart) {
                                return 'Not Yet Started';
                              } else if (ticket.actualStart && !ticket.actualEnd) {
                                return 'In Progress';
                              } else if (ticket.actualEnd) {
                                const plannedEnd = new Date(ticket.plannedEnd || ticket.plannedEndDate || '');
                                const actualEnd = new Date(ticket.actualEnd);
                                if (actualEnd <= plannedEnd) {
                                  return 'Completed On Time';
                                } else {
                                  return 'Completed Late';
                                }
                              }
                              return 'Unknown Status';
                            })()}
                          </p>
                          {ticket.actualStart && !ticket.actualEnd && (
                            <p className="text-xs text-blue-700 mt-1">
                              Started: {formatDateTime(ticket.actualStart)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {internalActiveTab === 'attachments' && (
                <AttachmentTab
                  entityType="case"
                  entityId={ticket.id}
                  canUpload={!isReadOnly}
                  canDelete={!isReadOnly}
                  ticketState={ticket.changeState}
                />
              )}

              {internalActiveTab === 'activity' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Activity History</h3>
                  {isLoadingActivities ? (
                    <TabLoadingSpinner label="Loading activity" />
                  ) : caseActivities && caseActivities.length > 0 ? (
                    <div className="space-y-3">
                      {caseActivities.map((activity: any) => {
                        // Format the activity display based on type
                        const renderActivityDetails = () => {
                          const meta = activity.metadata || {};

                          // Handle STATE_CHANGE activities
                          if (activity.activityType === 'STATE_CHANGE') {
                            // Check if it's an acknowledge action
                            if (meta.action === 'acknowledge') {
                              return (
                                <div className="mt-2 text-sm text-gray-700">
                                  <span className="font-medium">Acknowledged</span> - State changed from{' '}
                                  <span className="font-medium">{meta.previousState || activity.previousState || 'N/A'}</span> to{' '}
                                  <span className="font-medium">Acknowledged</span>
                                </div>
                              );
                            }
                            // Regular state change
                            return (
                              <div className="mt-2 text-sm text-gray-700">
                                State changed from <span className="font-medium">{meta.previousState || activity.previousState || 'N/A'}</span> to{' '}
                                <span className="font-medium">{meta.newState || activity.newState || 'N/A'}</span>
                              </div>
                            );
                          }

                          // Handle TICKET_TYPE_CONVERSION activities
                          if (activity.activityType === 'TICKET_TYPE_CONVERSION') {
                            return (
                              <div className="mt-2 text-sm text-gray-700">
                                <div>
                                  Converted from <span className="font-medium">{meta.previousTicketType || 'N/A'}</span> to{' '}
                                  <span className="font-medium">{meta.newTicketType || 'N/A'}</span>
                                </div>
                                {meta.conversionReason && (
                                  <div className="mt-1 text-sm italic text-gray-600">
                                    Reason: {meta.conversionReason}
                                  </div>
                                )}
                                {meta.newState && meta.previousState && meta.newState !== meta.previousState && (
                                  <div className="mt-1 text-sm text-gray-600">
                                    State also changed from <span className="font-medium">{meta.previousState}</span> to{' '}
                                    <span className="font-medium">{meta.newState}</span>
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // Handle ASSIGNMENT_CHANGE activities
                          if (activity.activityType === 'ASSIGNMENT_CHANGE') {
                            return (
                              <div className="mt-2 text-sm text-gray-700">
                                Assignment changed
                                {meta.previousAssignee && (
                                  <div>From: <span className="font-medium">{meta.previousAssignee}</span></div>
                                )}
                                {meta.newAssignee && (
                                  <div>To: <span className="font-medium">{meta.newAssignee}</span></div>
                                )}
                              </div>
                            );
                          }

                          // Handle COMMENT_ADDED activities
                          if (activity.activityType === 'COMMENT_ADDED' || activity.activityType === 'WORK_NOTE_ADDED') {
                            return (
                              <div className="mt-2 text-sm text-gray-700">
                                {activity.activityType === 'COMMENT_ADDED' ? 'Comment added' : 'Work note added'}
                                {meta.isPublic && <span className="ml-2 text-green-600">(Public)</span>}
                                {meta.note && (
                                  <div className="mt-1 p-2 bg-gray-100 rounded text-sm">
                                    <RichTextDisplay content={meta.note} />
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // For other activity types, show a more user-friendly display
                          if (meta && Object.keys(meta).length > 0) {
                            // Filter out technical fields
                            const displayableEntries = Object.entries(meta).filter(
                              ([key]) => !['convertedBy', 'performedBy', 'id'].includes(key)
                            );

                            if (displayableEntries.length > 0) {
                              return (
                                <div className="mt-2 text-sm text-gray-700 space-y-1">
                                  {displayableEntries.map(([key, value]) => {
                                    // Format the key to be more readable
                                    const formattedKey = key
                                      .replace(/([A-Z])/g, ' $1')
                                      .replace(/_/g, ' ')
                                      .replace(/^\w/, c => c.toUpperCase())
                                      .trim();

                                    // Format the value
                                    let formattedValue: string;
                                    if (typeof value === 'boolean') {
                                      formattedValue = value ? 'Yes' : 'No';
                                    } else if (typeof value === 'object') {
                                      formattedValue = JSON.stringify(value);
                                    } else {
                                      formattedValue = String(value);
                                    }

                                    return (
                                      <div key={key}>
                                        <span className="font-medium text-gray-600">{formattedKey}:</span>{' '}
                                        <span className="text-gray-900">{formattedValue}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            }
                          }

                          return null;
                        };

                        return (
                          <div key={activity.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <span className="text-sm font-medium text-gray-900">
                                  {activity.activityType.replace(/_/g, ' ').toUpperCase()}
                                </span>
                                <p className="text-sm text-gray-600 mt-1">
                                  by {activity.User?.firstName} {activity.User?.lastName}
                                </p>
                              </div>
                              <span className="text-xs text-gray-500">
                                {formatDate(activity.performedAt)}
                              </span>
                            </div>
                            {renderActivityDetails()}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500">No activity history available</p>
                  )}
                </div>
              )}

              {/* Closure Information Tab - Only for CHANGE tickets */}
              {internalActiveTab === 'closure' && ticket.ticketType === 'CHANGE' && (
                <div className="space-y-6">
                  {/* Change Status */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Change Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-gray-500">Current State</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                          ticket.changeState === 'closed' ? 'bg-green-100 text-green-800' :
                          ticket.changeState === 'cancelled' ? 'bg-red-100 text-red-800' :
                          ticket.changeState === 'review' ? 'bg-yellow-100 text-yellow-800' :
                          ticket.changeState === 'implement' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {ticket.changeState ? ticket.changeState.charAt(0).toUpperCase() + ticket.changeState.slice(1) : 'Unknown'}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500">Last Updated</label>
                        <p className="text-sm text-gray-900 mt-1">
                          {formatDateTime(ticket.updatedAt)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Implementation Results */}
                  {(ticket.changeState === 'implement' || ticket.changeState === 'review' || ticket.changeState === 'closed') && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Implementation Results</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-900">
                          <RichTextDisplay content={ticket.implementationResults || 'No implementation results recorded'} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Review Results */}
                  {(ticket.changeState === 'review' || ticket.changeState === 'closed') && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Review Results</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-900">
                          <RichTextDisplay content={ticket.reviewResults || 'No review results recorded'} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Closure Notes */}
                  {(ticket.changeState === 'closed' || ticket.changeState === 'cancelled') && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Closure Notes</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-900">
                          <RichTextDisplay content={ticket.closureNotes || ticket.resolution || 'No closure notes recorded'} />
                        </div>
                      </div>
                      {ticket.resolutionCode && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Code</label>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-sm text-gray-900">{ticket.resolutionCode}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Success Criteria - Only for closed changes */}
                  {ticket.changeState === 'closed' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-green-900 mb-2">Success Criteria</h4>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                          <span className="text-sm text-green-800">Change implemented successfully</span>
                        </div>
                        {ticket.testPlan && (
                          <div className="flex items-center">
                            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                            <span className="text-sm text-green-800">Test plan executed and validated</span>
                          </div>
                        )}
                        {ticket.backoutPlan && (
                          <div className="flex items-center">
                            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                            <span className="text-sm text-green-800">Backout plan verified and available</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cancellation Reason - Only for cancelled changes */}
                  {ticket.changeState === 'cancelled' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-red-900 mb-2">Change Cancelled</h4>
                      <p className="text-sm text-red-800">
                        This change has been cancelled. Please refer to the work notes for cancellation reasons and any follow-up actions required.
                      </p>
                      {ticket.cancellationReason && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-red-900 mb-1">Cancellation Reason</label>
                          <div className="text-sm text-red-800 bg-red-100 p-2 rounded">
                            <RichTextDisplay content={ticket.cancellationReason} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Change Timeline */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Change Timeline</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Created</span>
                        <span className="text-sm text-gray-900">{formatDateTime(ticket.createdAt)}</span>
                      </div>
                      {(ticket.plannedStart || ticket.plannedStartDate) && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Planned Start</span>
                          <span className="text-sm text-gray-900">
                            {formatDateTime(ticket.plannedStart || ticket.plannedStartDate || '')}
                          </span>
                        </div>
                      )}
                      {ticket.actualStart && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Actual Start</span>
                          <span className="text-sm text-gray-900">{formatDateTime(ticket.actualStart)}</span>
                        </div>
                      )}
                      {(ticket.plannedEnd || ticket.plannedEndDate) && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Planned End</span>
                          <span className="text-sm text-gray-900">
                            {formatDateTime(ticket.plannedEnd || ticket.plannedEndDate || '')}
                          </span>
                        </div>
                      )}
                      {ticket.actualEnd && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Actual End</span>
                          <span className="text-sm text-gray-900">{formatDateTime(ticket.actualEnd)}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t pt-2">
                        <span className="text-sm text-gray-500">Last Updated</span>
                        <span className="text-sm text-gray-900">{formatDateTime(ticket.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right side: Timeline and Related Information */}
        <div className="space-y-6">
          {/* Timeline */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Timeline</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(ticket.createdAt)}</p>
                </div>
              </div>
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <p className="text-sm text-gray-500">Last Updated</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(ticket.updatedAt)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Related Information */}
          {(ticket.parent || ticket.Engagement || ticket.Resource || (ticket.ticketType === 'CHANGE' && ticket.Customer)) && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Related Information</h3>
              <div className="space-y-4">
                {ticket.parent && (
                  <div>
                    <p className="text-sm text-gray-500">Parent Ticket</p>
                    <p className="text-sm font-medium text-gray-900">
                      {ticket.parent.number} - {stripHtml(ticket.parent.shortDescription)}
                    </p>
                  </div>
                )}
                {ticket.Customer && ticket.ticketType === 'CHANGE' && (
                  <div>
                    <p className="text-sm text-gray-500">Customer</p>
                    <p className="text-sm font-medium text-gray-900">{ticket.Customer.accountName}</p>
                  </div>
                )}
                {ticket.Engagement && (
                  <div>
                    <p className="text-sm text-gray-500">Engagement</p>
                    <p className="text-sm font-medium text-gray-900">{ticket.Engagement.name}</p>
                  </div>
                )}
                {ticket.Resource && (
                  <div>
                    <p className="text-sm text-gray-500">Resource</p>
                    <p className="text-sm font-medium text-gray-900">{ticket.Resource.name}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      <Transition.Root show={showApprovalModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowApprovalModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                        approvalModalData.decision === 'approved' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {approvalModalData.decision === 'approved' ? (
                          <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                          </svg>
                        )}
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          {approvalModalData.decision === 'approved' ? 'Approve Change Request' : 'Reject Change Request'}
                        </Dialog.Title>
                        <div className="mt-4">
                          <label htmlFor="approval-comments" className="block text-sm font-medium text-gray-700">
                            Comments (optional)
                          </label>
                          <RichTextEditor
                            value={approvalModalData.comments}
                            onChange={(value) => setApprovalModalData(prev => ({ ...prev, comments: value }))}
                            placeholder="Enter your comments..."
                            minHeight="120px"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${
                        approvalModalData.decision === 'approved'
                          ? 'bg-green-600 hover:bg-green-500'
                          : 'bg-red-600 hover:bg-red-500'
                      }`}
                      onClick={() => {
                        approvalMutation.mutate({
                          decision: approvalModalData.decision,
                          comments: approvalModalData.comments
                        });
                      }}
                      disabled={approvalMutation.isPending}
                    >
                      {approvalMutation.isPending ? 'Processing...' : (approvalModalData.decision === 'approved' ? 'Approve' : 'Reject')}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => setShowApprovalModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Customer Approval Modal */}
      <Transition.Root show={showCustomerApprovalModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowCustomerApprovalModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                        customerApprovalDecision === 'approved' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {customerApprovalDecision === 'approved' ? (
                          <CheckCircleIcon className="h-6 w-6 text-green-600" />
                        ) : (
                          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                          </svg>
                        )}
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          {customerApprovalDecision === 'approved' ? 'Approve Change Request' : 'Reject Change Request'}
                        </Dialog.Title>
                        <div className="mt-4">
                          <label htmlFor="customer-approval-comments" className="block text-sm font-medium text-gray-700 mb-2">
                            Comments {customerApprovalDecision === 'rejected' && <span className="text-red-600">*</span>}
                          </label>
                          <RichTextEditor
                            value={customerApprovalComments}
                            onChange={setCustomerApprovalComments}
                            placeholder={customerApprovalDecision === 'approved' ? 'Add optional comments...' : 'Please explain why you are rejecting this change...'}
                            minHeight="120px"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      disabled={customerApprovalDecision === 'rejected' && !customerApprovalComments.trim()}
                      className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${
                        customerApprovalDecision === 'approved'
                          ? 'bg-green-600 hover:bg-green-500 disabled:bg-green-300'
                          : 'bg-red-600 hover:bg-red-500 disabled:bg-red-300'
                      } disabled:cursor-not-allowed`}
                      onClick={() => {
                        customerApprovalMutation.mutate({
                          decision: customerApprovalDecision,
                          comments: customerApprovalComments
                        });
                      }}
                    >
                      {customerApprovalDecision === 'approved' ? 'Confirm Approval' : 'Confirm Rejection'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => {
                        setShowCustomerApprovalModal(false);
                        setCustomerApprovalComments('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default ChangeUnifiedTemplate;